import bindAllOfThis from 'src/utils/BindThisHelper';
import ImageApi from 'src/utils/ImageHelper';
import { PhaserButton, PhaserButtonConfig } from '../../shared/Button';
import TranslateApi from 'src/utils/TranslateHelper';
import { Color } from 'src/games/shared/ColorValue';
import { TiledGameObject } from '../../shared/phaserHelpers/HandyTypes';
import { EndGameScene } from 'src/games/DesertTerraformGame/scenes/EndGameScene';

// Paintbrush helper class to hold drawing attributes
class PaintBrush {
    public thickness: number;
    public color: number;
    public alpha: number;

    // default color yellow, thickness 5
    constructor() {
        this.thickness = 50;
        this.color = 0x00eded;
        this.alpha = 1;
    }
}

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    // Easel variables
    private easel: Phaser.GameObjects.Image;
    private easelControls: Phaser.GameObjects.Image;
    // Paintbrush holds draw stroke attributes
    private paintBrush: PaintBrush = new PaintBrush();
    // Arrays to hold history and colors
    private lineHistory: Phaser.Geom.Point[];
    // Line Items
    private makeLine: boolean;
    private buttonPressed: boolean = false;
    private continueLine: boolean = false;
    private isDragging: boolean = false;
    private usingWaterTool: boolean = false;
    private resizeIcon: Phaser.GameObjects.Image;
    private resizing: boolean;
    private finishButton: PhaserButton;
    private waterButton: Phaser.GameObjects.Image;
    private eraseButton: Phaser.GameObjects.Image;
    private defaultButtonSize: Phaser.Geom.Rectangle;
    private itemOptions: Phaser.GameObjects.Image[];
    private easelGraphic: Phaser.GameObjects.Graphics;
    private easelTexture: Phaser.GameObjects.RenderTexture;
    // Screenshot Variables
    private div: HTMLDivElement = document.createElement('div');

    private currentImageFrame: string = "";
    private currentImage: Phaser.GameObjects.Image;
    private paste: Phaser.GameObjects.Image;
    private pastedImages: Phaser.GameObjects.Image[];
    private paintBrushCursor: string = 'url(' + require("/src/assets/qci/sprites/cursors/paint_brush.cur") + '), pointer';

    private DESERT_FLOOR_COLOR: number = 0xC8AD55;
    public static CROPPED_IMAGE: string = null;

    constructor() {
        super({
            key: "MainScene"
        });
        bindAllOfThis(this, MainScene.prototype);
    }

    preload(): void {
        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.atlas("assets", require('/src/assets/qci/atlas/gameTerraform.png'), require('/src/assets/qci/atlas/gameTerraform.json'));
        this.load.tilemapTiledJSON('map', require("/src/assets/qci/maps/desert_terraform.json"));
    }

    create(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: 'map' });
        var easelArea = new Phaser.Geom.Rectangle(0, 0, this.sys.canvas.width - 400, this.sys.canvas.height);
        var controlArea = new Phaser.Geom.Rectangle(easelArea.width, 0, 400, this.sys.canvas.height);
        this.defaultButtonSize = new Phaser.Geom.Rectangle(0, 0, 100, 100);
        this.itemOptions = [];
        this.lineHistory = [];
        this.pastedImages = [];
        this.makeLine = false;
        this.buildControlArea(controlArea);
        this.buildControls(controlArea);
        this.buildEasel(easelArea);
        this.addResizeIcon();
        document.body.appendChild(this.div);

        // Register the click events
        this.registerEvents();
    }

    addObjects(): void {
        this.map.getObjectLayer("Objects").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "pilot") {
                var pilot = this.add.image(ob.x, ob.y, "pilots", "a");
                pilot.setOrigin(0);
                pilot.setDepth(1);
                pilot.setDisplaySize(ob.width, ob.height);
            }
            else if (ob.name === "water") {
                this.waterButton = this.add.image(ob.x, ob.y, "assets", "tool-water");
                this.waterButton.setOrigin(0);
                this.waterButton.setDepth(1);
                this.waterButton.setInteractive();
                this.waterButton.setDisplaySize(ob.width, ob.height);
                this.waterButton.on("pointerdown", () => {
                    this.input.setDefaultCursor(this.paintBrushCursor);
                    this.resizing = false;
                    this.usingWaterTool = true;
                    this.buttonPressed = true;
                    this.currentImageFrame = "";
                    this.currentImage.setVisible(false);
                    this.waterButton.setAlpha(1);
                    this.eraseButton.setAlpha(0.5);
                    this.greyOutOtherItems();
                    this.paintBrush.color = 0x00eded;
                    this.refreshPaintBrush();
                });
            }
            else if (ob.name === "erase") {
                this.eraseButton = this.add.image(ob.x, ob.y, "assets", "tool-erase");
                this.eraseButton.setOrigin(0);
                this.eraseButton.setDepth(1);
                this.eraseButton.setTint(0xC8AD55);
                this.eraseButton.setInteractive();
                this.eraseButton.setDisplaySize(ob.width, ob.height);
                this.eraseButton.on("pointerdown", () => {
                    this.input.setDefaultCursor(this.paintBrushCursor);
                    this.resizing = false;
                    this.usingWaterTool = true;
                    this.buttonPressed = true;
                    this.currentImageFrame = "";
                    this.currentImage.setVisible(false);
                    this.eraseButton.setAlpha(1);
                    this.waterButton.setAlpha(0.5);
                    this.greyOutOtherItems();
                    this.paintBrush.color = this.DESERT_FLOOR_COLOR;
                    this.refreshPaintBrush();
                });
            }
            else if (ob.name === "done") {
                var config = new PhaserButtonConfig();
                config.backgroundColor = Color.KUDER_PRIMARY_BUTTON;
                config.smartSize = true;

                this.finishButton = new PhaserButton(this, ob.x, ob.y, 100, 50, TranslateApi.translate("common.done"), config);
                this.finishButton.setDepth(1);
                this.finishButton.on("buttonClicked", () => {
                    this.buttonPressed = false;
                    this.usingWaterTool = false;
                    this.currentImageFrame = "";
                    // Screenshot for later
                    this.registry.parent.renderer.snapshot((image: HTMLImageElement) => {
                        image.onload = () => {
                            var croppedImage = ImageApi.CropImageToDataURI(image, 0, 0, this.sys.canvas.width - 300, this.sys.canvas.height);
                            console.warn("Screenshot!", croppedImage); // JB - Remove this console.warn
                            this.endGame(croppedImage);
                        };
                    });
                });
            }
            else {
                var button = this.add.image(ob.x, ob.y, "assets", ob.name);
                button.setOrigin(0);
                button.setDepth(1);
                button.setInteractive();
                button.setDisplaySize(button.width * 0.7, button.height * 0.7);
                button.on("pointerdown", () => {
                    this.resizing = false;
                    this.buttonPressed = false;
                    this.usingWaterTool = false;
                    this.currentImageFrame = ob.name;
                    this.currentImage.setFrame(this.currentImageFrame);
                    this.currentImage.setVisible(false);
                    this.waterButton.setAlpha(0.5);
                    this.greyOutOtherItems();
                });
                this.itemOptions.push(button);
            }
        });
        this.currentImage = this.add.image(this.easelControls.x + 170, this.easelControls.y + 625, "assets", "");
        this.currentImage.setOrigin(.5);
        this.currentImage.setDepth(1);
        this.currentImage.setVisible(false);
    }

    buildEasel(easelArea: Phaser.Geom.Rectangle): void {
        this.easel = this.add.image(easelArea.x, easelArea.y, "pixel");
        this.easel.setOrigin(0, 0);
        this.easel.setDisplaySize(easelArea.width, easelArea.height);
        this.easel.setTint(this.DESERT_FLOOR_COLOR);
        this.easel.setDepth(-1);
        this.easel.setInteractive();
        this.easel.on('pointerdown', (pointer) => {
            this.pasteImage(pointer);
        }, this);

        this.easelTexture = this.add.renderTexture(0, 0, this.sys.canvas.width - 300, this.sys.canvas.height);
        this.easelTexture.setOrigin(0);
        this.easelTexture.setDepth(0);

        this.easelGraphic = this.add.graphics();
        this.easelGraphic.setDepth(0);
    }

    buildControlArea(controlArea: Phaser.Geom.Rectangle) {
        this.easelControls = this.add.image(controlArea.x, controlArea.y, "pixel");
        this.easelControls.setTint(0x551A8B);
        this.easelControls.setOrigin(0, 0);
        this.easelControls.setDisplaySize(controlArea.width, controlArea.height);
        this.easelControls.setDepth(1);
    }

    buildControls(controlArea: Phaser.Geom.Rectangle): void {
        this.addObjects();
    }

    pasteImage(pointer) {
        if (this.currentImageFrame !== "") {
            this.paste = this.add.image(pointer.x, pointer.y, "assets", this.currentImageFrame);
            this.paste.setInteractive();
            this.paste.depth = this.easel.depth + 1;
            this.paste.setDisplaySize(this.defaultButtonSize.width / 1.5, this.defaultButtonSize.height / 1.5);
            this.input.setDraggable(this.paste);
            this.pastedImages.push(this.paste);
        }
    }

    greyOutOtherItems(): void {
        this.itemOptions.forEach((i: Phaser.GameObjects.Image) => {
            if (i.frame.name !== this.currentImageFrame) {
                i.setAlpha(0.5);
            }
            else {
                i.setAlpha(1);
            }
        });
    }

    refreshPaintBrush() {
        this.easelGraphic.lineStyle(this.paintBrush.thickness, this.paintBrush.color, this.paintBrush.alpha);
    }

    registerEvents(): void {
        this.input.on('pointerdown', this.pointerDown);
        this.input.on('pointerup', this.pointerUp);
        this.input.on('pointermove', this.pointerMove);
        // If pointer goes up outside the canvas, stop drawing
        document.addEventListener('mouseup', this.pointerUp);
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image) => {
            this.children.bringToTop(gameObject);
            this.isDragging = true;
            if (gameObject.name !== "resizeIcon") {
                this.paste = gameObject;
            }
            else {
                gameObject.x = pointer.downX;
                gameObject.y = pointer.downY;
            }

            this.buttonPressed = false;
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image, dragX: number, dragY: number) => {
            if (this.resizing) {
                var corner = this.getBottomRightCorner();
                this.resizeForBottomRightCorner(pointer, corner);
            }
            else {
                gameObject.x = pointer.x;
                gameObject.y = pointer.y;
            }
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image) => {
            var doesIntersect = Phaser.Geom.Intersects.RectangleToRectangle(gameObject.getBounds(), this.easelControls.getBounds());
            if (doesIntersect && !this.resizing) {
                gameObject.destroy();
            }
            else {
                this.children.moveDown(gameObject);
            }

            if (this.usingWaterTool) {
                this.buttonPressed = true;
                this.resizing = false;
            }

            if (this.resizing) {
                this.buttonPressed = false;
                this.resizing = false;
            }
            this.isDragging = false;
        });
    }

    addResizeIcon(): void {
        this.resizeIcon = this.add.image(-100, 0, "assets", "tool-resize");
        this.resizeIcon.setOrigin(0);
        this.resizeIcon.setTint(0x000000);
        this.resizeIcon.setDepth(10);
        this.resizeIcon.setName("resizeIcon");

        this.resizeIcon.setDisplaySize(30, 30);

        // Make the resize icon larger for touchscreens Gotta support fatter fingers :)
        if (this.sys.game.device.input.touch) {
            this.resizeIcon.setDisplaySize(75, 75);
        }

        this.resizeIcon.setInteractive();
        this.resizeIcon.on('pointerdown', () => {
            this.resizing = true;
            this.makeLine = false;
            this.buttonPressed = false;
            this.currentImage.setVisible(false);
            this.children.bringToTop(this.paste);
            this.children.bringToTop(this.resizeIcon);
        });
        this.input.setDraggable(this.resizeIcon);
    }

    getBottomRightCorner(): Phaser.Geom.Point {
        return this.paste.getBottomRight();
    }

    getDeltaWidth(): number {
        return this.paste.displayWidth * 0.03;
    }
    getDeltaHeight(): number {
        return this.paste.displayHeight * 0.03;
    }

    getMaxHeight(): number {
        return this.paste.height * 2;
    }

    getMaxWidth(): number {
        return this.paste.width * 2;
    }

    getMinHeight(): number {
        return this.paste.height * 0.1;
    }

    getMinWidth(): number {
        return this.paste.width * 0.1;
    }

    resizeForBottomRightCorner(pointer: Phaser.Input.Pointer, corner: Phaser.Geom.Point): void {
        // Dragging right and down (increase size)
        if (pointer.x > corner.x && pointer.y > corner.y) {
            if (((this.paste.displayWidth + this.getDeltaWidth()) <= this.getMaxWidth()) &&
                ((this.paste.displayHeight + this.getDeltaHeight()) <= this.getMaxHeight())) {
                this.paste.displayWidth += this.getDeltaWidth();
                this.paste.displayHeight += this.getDeltaHeight();
            }
        }
        // Dragging left and up (decrease size)
        if (pointer.x < corner.x && pointer.y < corner.y) {
            if (((this.paste.displayWidth - this.getDeltaWidth()) >= this.getMinWidth()) &&
                ((this.paste.displayHeight - this.getDeltaHeight()) >= this.getMinHeight())) {
                this.paste.displayWidth -= this.getDeltaWidth();
                this.paste.displayHeight -= this.getDeltaHeight();
            }
        }

    }

    pointerDown(pointer: Phaser.Input.Pointer): void {
        if (this.buttonPressed && pointer.x <= this.easelControls.x) {
            this.makeLine = true;
            this.lineHistory = [];
        }
    }

    pointerUp(): void {
        this.makeLine = false;
        // Capture the graphics as a texture
        this.easelGraphic.generateTexture("background-drawn", this.sys.canvas.width - 300, this.sys.canvas.height);
        this.easelGraphic.clear();
        let tempImage: Phaser.GameObjects.Image = this.add.image(0, 0, "background-drawn");
        this.easelTexture.save();
        this.easelTexture.draw(tempImage.texture, tempImage.frame, 0, 0);
        this.easelTexture.restore();
        tempImage.destroy();
    }

    pointerMove(pointer: Phaser.Input.Pointer): void {
        if (this.makeLine) {
            // Show the shape being drawn as the mouse moves
            this.easelGraphic.clear();
            this.refreshPaintBrush();
            this.lineHistory.push(new Phaser.Geom.Point(pointer.x, pointer.y));
            // Do the drawing
            this.easelGraphic.strokePoints(this.lineHistory);
        }

        this.currentImage.x = pointer.x;
        this.currentImage.y = pointer.y;
        this.currentImage.setDisplaySize(48, 48);
        if (this.isMovingExistingImage(pointer) && !this.resizing) {
            this.currentImage.setVisible(false);
            if (this.isDragging) {
                this.input.setDefaultCursor('-webkit-grabbing');
            }
            else {
                this.input.setDefaultCursor('-webkit-grab');
            }
        }
        else if (this.isResizingImage(pointer) || this.resizing) {
            this.currentImage.setVisible(false);
            this.input.setDefaultCursor('nw-resize');
        }
        else if (this.currentImageFrame !== "") {
            this.currentImage.setVisible(true);
            this.input.setDefaultCursor('none');
        }
        else if (this.usingWaterTool) {
            this.currentImage.setVisible(false);
            this.input.setDefaultCursor(this.paintBrushCursor);
        }
    }

    disableEverything(): void {
        this.itemOptions.forEach((i) => {
            i.input.enabled = false;
        });
        this.waterButton.input.enabled = false;
        this.finishButton.setEnabled(false);
    }


    endGame(image: string): void {
        document.removeEventListener('mouseup', this.pointerUp);
        MainScene.CROPPED_IMAGE = image;

        this.input.setDefaultCursor("default");
        this.input.removeAllListeners();
        this.scene.add("EndGameScene", EndGameScene, true);
        this.scene.remove(this);
    }

    update(): void {
        if (!this.input.enabled) { return; }
        // only show resize icon if not drawing and it's not in the control area (just deleted something)
        this.resizeIcon.setVisible((this.currentImageFrame !== "" && !this.isOnControlArea()));
        if (this.paste != null) {
            var bottomCorner = this.getBottomRightCorner();
            this.resizeIcon.setPosition(bottomCorner.x, bottomCorner.y);
        }

        // If you go over to easel, change pointer
        if (this.input.activePointer.x > this.easelControls.x) {
            this.currentImage.setVisible(false);
            this.input.setDefaultCursor('pointer');
            this.children.bringToTop(this.resizeIcon);
        }
    }

    isOnWater(): boolean {
        return this.waterButton.getBounds().contains(this.input.activePointer.x, this.input.activePointer.y);
    }

    isOnControlArea() {
        return Phaser.Geom.Intersects.RectangleToRectangle(this.resizeIcon.getBounds(), this.easelControls.getBounds());
    }

    isResizingImage(pointer: Phaser.Input.Pointer): boolean {
        return this.resizeIcon.getBounds().contains(pointer.x, pointer.y);
    }

    isMovingExistingImage(pointer: Phaser.Input.Pointer): boolean {
        for (var i of this.pastedImages) {
            if (i.getBounds().contains(pointer.x, pointer.y)) {
                return true;
            }
        }
        return false;
    }
}



// WEBPACK FOOTER //
// ./src/games/DesertTerraformGame/scenes/MainScene.ts